import { Children, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import get from 'lodash-es/get'
import isArray from 'lodash-es/isArray'

import { QA_NAME_CLEAR_BTN } from '../../../constants/qa'
import { getQaSelector } from '../../../utils/common'
import Button, { BUTTON_HEIGHT_20 } from '../../Button'
import { Tag, TAG_APPEARANCE, TAG_HEIGHT } from '../../Tag'
import { DEFAULT_STYLE, MAX_SIZE_COLUMN } from '../Accordion.constants'
import { getFilterItemLabel } from '../Accordion.utils'
import { AccordionButton } from '../AccordionButton'

import { QA_NAME_ACCORDION_SECTION_CONTEXT } from './Accordion.constants'

import * as styles from './Accordion.module.css'

function Accordion({
  ButtonComponent = AccordionButton,
  children,
  className,
  defaultOpen,
  disabled,
  doubleClassName,
  entity,
  itemClassName,
  maxSize = MAX_SIZE_COLUMN,
  query,
  shouldAdaptToMobileScreen,
  singleColumn,
  variant = DEFAULT_STYLE,
  onClear,
}) {
  const { t } = useTranslation()

  const initialSet = defaultOpen ? [0] : []

  const [openIds, setOpenIds] = useState(new Set(initialSet))

  function handleOpen(id) {
    setOpenIds(new Set(openIds.add(id)))
  }

  function handleClick(id) {
    setOpenIds(currOpenIds => {
      const nextOpenIds = new Set([...currOpenIds])

      if (openIds.has(id)) {
        nextOpenIds.delete(id)
      } else {
        nextOpenIds.add(id)
      }

      return nextOpenIds
    })
  }

  const count = Children.count(children)

  const items = Children.map(children, (item, index) => {
    if (!item) {
      return null
    }

    const {
      props: {
        accordionTitle,
        filterKey,
        filterSecondKey,
        listType,
        staticCount,
        onClick,
      },
    } = item

    if (!accordionTitle) {
      return item
    }

    function handleClear() {
      onClear(filterKey, filterSecondKey)
    }

    const currentFilterKey = get(query, filterKey) ? filterKey : filterSecondKey

    function getFilterCount() {
      if (staticCount) {
        return staticCount
      }

      if (!query || !query[currentFilterKey]) {
        return 0
      }

      if (isArray(query[currentFilterKey])) {
        return query[currentFilterKey].length
      }

      return 1
    }

    const itemClass = classNames(styles.item, itemClassName, {
      [className]: count <= 6,
      [doubleClassName]: count > 6,
    })

    const contentClassName = classNames(styles.content, {
      [styles.content1]: variant === 'style1',
      [styles.content2]: variant === 'style2',
    })

    const buttonProps = {
      ...item.props,
      disabled,
      entity,
      getFilterCount,
      index,
      open: openIds.has(index),
      variant,
      onClick: () => handleClick(index),
      onOpen: handleOpen,
    }

    const sectionProps = getQaSelector({
      context: QA_NAME_ACCORDION_SECTION_CONTEXT,
      value: item.props.qaName,
    })

    return (
      <div {...sectionProps} className={itemClass}>
        <ButtonComponent {...buttonProps} />
        {openIds.has(index) && (
          <div className={contentClassName}>
            {item}

            {query && query[currentFilterKey] && (
              <div className={styles.filters}>
                <Button.Contained
                  isLight
                  className={styles.clear}
                  height={BUTTON_HEIGHT_20}
                  qaName={QA_NAME_CLEAR_BTN}
                  text={t('Filter.ClearAllBtnKey')}
                  onClick={handleClear}
                />
                {isArray(query[currentFilterKey]) ? (
                  query[currentFilterKey].map(filter => {
                    return (
                      <Tag
                        appearance={TAG_APPEARANCE.filled}
                        className={styles.tag}
                        height={TAG_HEIGHT.height20}
                        key={filter.value}
                        text={getFilterItemLabel({
                          entity,
                          filterKey,
                          filterSecondKey,
                          listType,
                          option: filter,
                          query,
                        })}
                        onRemove={() => onClick(filter)}
                      />
                    )
                  })
                ) : (
                  <Tag
                    appearance={TAG_APPEARANCE.filled}
                    className={styles.tag}
                    height={TAG_HEIGHT.height20}
                    text={getFilterItemLabel({
                      entity,
                      filterKey,
                      filterSecondKey,
                      listType,
                      option: query[currentFilterKey],
                      query,
                    })}
                    onRemove={() => onClick(query[currentFilterKey])}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  })

  if (count <= maxSize || singleColumn) {
    return items
  }

  const firstSectionLength = Math.ceil(count / 2)

  return (
    <div
      className={classNames(styles.box, {
        [styles.mobile]: shouldAdaptToMobileScreen,
      })}
    >
      <div
        className={classNames(styles.section, {
          [styles.mobile]: shouldAdaptToMobileScreen,
        })}
      >
        {items.slice(0, firstSectionLength)}
      </div>
      <div>{items.slice(firstSectionLength)}</div>
    </div>
  )
}

export default Accordion
